<script setup>
  import { ref, computed, onMounted, getCurrentInstance } from "vue";
  import { useRoute, useRouter } from "vue-router";
  import { usePageLoadingStore } from "../../../../../stores/pageLoading";
  import adminJobFairJobNoticeService from "../../../services/jobfair/adminJobFairJobNoticeService";
  import JobFairJobNoticeDetailModal from "../../../../user/components/modal/jobFair/JobFairJobNoticeDetailModal.vue";
  import CorpJobFairJobPostingsApplicantListView from "./CorpJobFairJobPostingsApplicantListView.vue";
  import mypageJobFairJobNoticeService from "../../../../user/services/mypage/jobFair/mypageJobFairJobNoticeService";
  import JobFairResumeDetailModal from "../../../../user/components/modal/jobFair/JobFairResumeDetailModal.vue";

  const { $pageListBoardNo, $toast, $number, $gbNameKR, $yyyymmddHHmmss, alert, confirm } = getCurrentInstance().appContext.config.globalProperties;

  const route = useRoute();
  const router = useRouter();
  const loadingStore = usePageLoadingStore();

  const currentPage = ref(1);
  const pageSize = ref(10);
  const totalPages = ref(1);
  const totalCount = ref(0);
  const list = ref([]);

  const isApplicantView = ref(false);
  const selectedJobNoticeSeq = ref('');
  const selectedCorpId = ref('');

  // 모달
  const jobNoticeModal = ref(null);
  const resumeModal = ref(null);

  const pageSizeGroup = ref([
    { label:"10개씩 보기", value: 10 },
    { label:"30개씩 보기", value: 30 },
    { label:"50개씩 보기", value: 50 },
    { label:"100개씩 보기", value: 100 },
  ])

  const onSelectPage = (pageNo) => {
    currentPage.value = pageNo;
    retrieveList();
  }

  // 페이지당 개수 변경
  const onSelectPageSize = (option) => {
    pageSize.value = option.value;
    currentPage.value = 1;
    retrieveList();
  }

  const retrieveList = () => {
    let paramObj = {
      corpId: route.params.id,
      pageNumber: currentPage.value,
      size: pageSize.value,
    }
    loadingStore.loading = true;
    adminJobFairJobNoticeService.retrieveJobNoticeListPagingByCorpId(paramObj)
      .then( async (res) => {
        loadingStore.loading = false;
        if (res.data.code.toLowerCase() === "success") {
          const result = res.data.result;
          totalPages.value = result.totalPages;
          totalCount.value = result.totalElements;
          list.value = $pageListBoardNo(result.content, totalCount.value, pageSize.value, currentPage.value);
        }
      })
      .catch((res) => {
        loadingStore.loading = false;
        $toast("error", res);
      });
  };

  // 입사지원 수 클릭시
  const onClickApplicantCnt = (jobNotice) => {
    selectedJobNoticeSeq.value = jobNotice.jobNoticeSeq;
    selectedCorpId.value = jobNotice.corpId;
    isApplicantView.value = true;
  }

  // 채용공고 보기
  const onClickJobNotice = async (jobNoticeSeq, corpId) => {
    console.log(jobNoticeSeq, corpId);
    await jobNoticeModal.value.show({
      jobNoticeSeq,
      corpId,
    });
  }

  // 이력서 보기
  const onClickResume = async (resumeId) => {
    await resumeModal.value.show({
      resumeId,
      showDeleteBtn: false,
    });
  }

  // 채용공고 수정
  const onClickEditJobNotice = (jobNotice) => {
    router.push({
      name: 'MyPageCorpJobNotice',
      query : {
        jobNoticeSeq: jobNotice.jobNoticeSeq,
        mode: 'admin',
        mode2: 'admin-corp',
        corpId: String(route.params.id || ''),
      }
    });
  };

  // 채용공고 삭제
  const onClickDeleteJobNotice = async (jobNotice) => {
    const ok = await confirm(["채용공고를 삭제하시겠습니까?"], "삭제");
    if(ok) {
      mypageJobFairJobNoticeService.deleteJobNotice({ jobNoticeSeq: jobNotice.jobNoticeSeq })
        .then( async (res) => {
          loadingStore.loading = false;
          if(res.data.code.toLowerCase() === 'success') {
            alert("채용공고가 삭제되었습니다.");
            retrieveList();
          } else {
            if (res.response.data.result.message) {
              $alert([res.response.data.result.message]);
            } else {
              $alert("채용공고 삭제를 실패하였습니다.");
            }
          }
        })
        .catch( (res) =>  {
          alert([res.response.data.result.message]);
          loadingStore.loading = false;
        });
    }
  };

  const openDetailInNewTab = (linkName, id = {}) => {
    const url = router.resolve({
      name: linkName,
      params: { id },
    }).href;

    window.open(url, '_blank');
  }

  onMounted(()=>{
    retrieveList();
  });
</script>

<template>
  <div v-if="!isApplicantView">
    <div class="list-top">
      <p>총 <span>{{ $number(totalCount)}}</span>개</p>
      <div>
        <KSASelect :options="pageSizeGroup" class="select" @selectOption="onSelectPageSize" />
      </div>
    </div>
    <div class="table-form">
      <table class="horizontal">
        <tr>
          <th>NO</th>
          <th>채용상태</th>
          <th>채용공고 제목</th>
          <th>입사지원</th>
          <th>채용기간</th>
          <th>최근 수정일</th>
          <th>등록일</th>
          <th>마스터</th>
          <th>관리</th>
        </tr>
        <tr v-for="jobNotice in list">
          <td>{{ jobNotice.boardNo }}</td>
          <td>{{ $gbNameKR('JOB_NOTICE_STATE_GB', jobNotice.jobNoticeStateGb) }}</td>
          <td class="clickable blue text-overflow long-overflow" @click="onClickJobNotice(jobNotice.jobNoticeSeq, jobNotice.corpId)">{{ jobNotice.jobName }}</td>
          <td class="clickable blue" @click="onClickApplicantCnt(jobNotice)">{{ jobNotice.applicantCnt }}</td>
          <td>{{ $yyyymmdd(jobNotice.recruitStartYmd) }} ~ {{ $yyyymmdd(jobNotice.recruitEndYmd) }}</td>
          <td>{{ $yyyymmddHHmmss(jobNotice.modifyDate) }}</td>
          <td>{{ $yyyymmddHHmmss(jobNotice.createDate) }}</td>
          <td class="clickable blue">
            <span @click="openDetailInNewTab('MemberDetail', jobNotice.createId)">{{ jobNotice.userName }}</span>
          </td>
          <td>
            <KSATextButton class="clickable" text="수정" @click="onClickEditJobNotice(jobNotice)"/>
            &nbsp;
            <KSATextButton v-if="jobNotice.applicantCnt === 0" class="clickable red_button" text="삭제" @click="onClickDeleteJobNotice(jobNotice)"/>
          </td>
        </tr>
      </table>
      <div class="result-none" v-if="list.length === 0">
        등록된 채용공고가 없습니다.
      </div>
    </div>
    <Paginate
      v-if="list.length > 0"
      v-model="currentPage"
      :page-count="totalPages"
      :click-handler="onSelectPage"
    />
  </div>
  <div v-if="isApplicantView">
    <CorpJobFairJobPostingsApplicantListView 
      :jobNoticeSeq="selectedJobNoticeSeq"
      :corpId="selectedCorpId"
      @go-back="isApplicantView = false;"
      @onClickJobNotice="onClickJobNotice"
      @onClickResume="onClickResume"
    />
  </div>
  <JobFairJobNoticeDetailModal ref="jobNoticeModal" /> <!-- 채용공고 보기 -->
  <JobFairResumeDetailModal ref="resumeModal" /> <!-- 이력서 보기 -->
</template>
<style scoped>
</style>
